import Immutable from 'immutable';
import { getAppServices } from 'providers/app-services';
import { ExportSortDirection } from 'records/Export';
import { NewProject } from 'records/NewProject';
import { Permissions } from 'records/Permissions';
import { Project, ProjectList, RawProject } from 'records/Project';
import { UpdateProject } from 'records/UpdateProject';
import GQLRequest from 'utils/GQLRequest';
import { LicensePackageOptions } from 'records/License';

const permissions = Object.keys(new Permissions().toObject()).join(`\n`);

export const getProject = async (
  projectid: string,
  licensePackage: LicensePackageOptions
): Promise<Project> => {
  const { project } = await new GQLRequest()
    .query(
      `
      query Project($projectid: String!, $licensePackage: String) {
        project(id: $projectid, licensePackage: $licensePackage) {
          id
          name
          disciplines
          assignees
          statuses
          priorities
          permissions {
            ${permissions}
          }
          createdBy
          createdDate
          code
          street
          city
          state
          zipcode
          geolocation {
            center
            bbox
          }
          Status
          projectType
          budget
          user {
            id
            email
            profile {
              firstname
              lastname
              company
              role
              discipline
              additionalinfoneeded
            }
          }
          creatorUser {
            id
            email
          }
          trialDaysRemaining
        }
      }
      `
    )
    .variables({ projectid, licensePackage })
    .json<{ project: RawProject }>();

  return Project.deserialize(project);
};

/*
NOTE: GetProjects call DOES NOT fetch project assignees as part of the payload.
      This is due to perf issues on the backend.
*/
export const getProjects = async (
  licensePackage: string
): Promise<ProjectList> => {
  const { projects } = await new GQLRequest()
    .query(
      `
      query Projects($licensePackage: String) {
        projects(licensePackage: $licensePackage) {
          id
          name
          disciplines
          statuses
          permissions {
            ${permissions}
          }
          createdBy
          createdDate
          code
          street
          city
          state
          zipcode
          geolocation {
            center
            bbox
          }
          Status
          projectType
          budget
          user {
            id
            email
            profile {
              firstname
              lastname
              company
              role
              discipline
              additionalinfoneeded
            }
          }
          creatorUser {
            id
            email
          }
        }
      }
      `
    )
    .variables({ licensePackage })
    .json<{ projects: RawProject[] }>();

  return Immutable.List(
    projects.map((project) => Project.deserialize(project))
  );
};

export const createProjectBasedTrial = async (
  newProject: NewProject
): Promise<Project> => {
  const { createProjectBasedTrial: newProjectData } = await new GQLRequest()
    .query(
      `
      mutation CreateProjectBasedTrial($newProject: NewProject!) {
        createProjectBasedTrial(input: $newProject) {
          id
          name
          disciplines
          assignees
          statuses
          priorities
          permissions {
            ${permissions}
          }
          createdBy
          createdDate
          code
          street
          city
          state
          zipcode
          Status
        }
      }
      `
    )
    .variables({ newProject: newProject.serialize() })
    .json<{ createProjectBasedTrial: RawProject }>();

  return Project.deserialize(newProjectData);
};

export const createProjectWithSubscription = async (
  newProject: NewProject
): Promise<Project> => {
  const { authService } = getAppServices();
  const { idToken } = authService.getCredentials();
  const { createProjectWithSubscription: newProjectData } =
    await new GQLRequest()
      .query(
        `
      mutation CreateProjectWithSubscription($newProject: NewProject!, $idToken: String!) {
        createProjectWithSubscription(input: $newProject, idToken: $idToken) {
          id
          name
          disciplines
          assignees
          statuses
          priorities
          permissions {
            ${permissions}
          }
          createdBy
          createdDate
          code
          street
          city
          state
          zipcode
          Status
        }
      }
      `
      )
      .variables({ newProject: newProject.serialize(), idToken })
      .json<{ createProjectWithSubscription: RawProject }>();

  return Project.deserialize(newProjectData);
};

export const updateProject = async (
  projectData: UpdateProject
): Promise<Project> => {
  const updateData = projectData.serialize();

  const { updateProject: updateProjectData } = await new GQLRequest()
    .query(
      `
      mutation UpdateProject($updateProject: UpdateProject!) {
        updateProject(input: $updateProject) {
          id
          name
          disciplines
          assignees
          statuses
          permissions {
            ${permissions}
          }
          createdBy
          createdDate
          code
          street
          city
          state
          zipcode
          Status
          projectType
          budget
          countryCode
      }
    }
    `
    )
    .variables({ updateProject: updateData })
    .json<{ updateProject: RawProject }>();

  return Project.deserialize(updateProjectData);
};

export const exportXLSXProject = async (
  projectid: string,
  issueids: string[],
  sortfield: string,
  sortdirection: ExportSortDirection
): Promise<{ url: string; extension: string }> => {
  const r = await new GQLRequest()
    .query(
      `
      query ExportXLSXProject($projectid: String!, $issueids: [String!], $sortfield: String, $sortdirection: ExportSortDirection) {
        export(projectid: $projectid, issueids: $issueids, sortfield: $sortfield, sortdirection: $sortdirection) {
          url
          extension
        }
      }
      `
    )
    .variables({ projectid, issueids, sortfield, sortdirection })
    .json<{ export: { url: string; extension: string } }>();

  return r.export;
};

export const exportPDFProject = async (
  projectid: string,
  issueids: string[],
  sortfield: string,
  sortdirection: ExportSortDirection
): Promise<{ url: string; extension: string }> => {
  const r = await new GQLRequest()
    .query(
      `
      query ExportPDFProject($input: ReportInput) {
        report(input: $input) {
          url
          extension
        }
      }
      `
    )
    .variables({ input: { projectid, issueids, sortfield, sortdirection } })
    .json<{ report: { url: string; extension: string } }>();

  return r.report;
};

export const getProjectDisciplines = async (
  projectid: string
): Promise<string[]> => {
  const { disciplines } = await new GQLRequest()
    .query(
      `
      query GetProjectDisciplines($projectid: String!) {
        disciplines(projectid: $projectid) {

        }
      }
      `
    )
    .variables({ projectid })
    .json<{ disciplines: string[] }>();

  return disciplines;
};

export interface ExportItems {
  drawings: boolean;
  punch: boolean;
  rfis: boolean;
  submittals: boolean;
  users: boolean;
}

export const exportProjectData = async (
  projectid: string,
  items: ExportItems
): Promise<{ url: string; extension: string }> => {
  const r = await new GQLRequest()
    .query(
      `
      query exportAllData($punch: Boolean, $drawings: Boolean, $rfis: Boolean, $projectid: String!, $submittals: Boolean, $userlog: Boolean){
      exportAllData(input: {punch: $punch, drawings: $drawings, rfis: $rfis, projectid: $projectid, submittals: $submittals, userlog: $userlog})	{
        url
        extension
        }
      }
  `
    )
    .variables({
      projectid,
      drawings: items.drawings,
      punch: items.punch,
      rfis: items.rfis,
      submittals: items.submittals,
      userlog: items.users,
    })
    .json<{ exportAllData: { url: string; extension: string } }>();

  return r.exportAllData;
};

export default {
  getProject,
  getProjects,
  createProjectBasedTrial,
  exportXLSXProject,
  exportPDFProject,
  getProjectDisciplines,
  exportProjectData,
};
